import type { CustomerInfo as CustomerInfoType } from '@types';
import { HelperMethods } from '_utils';
import React, { useState } from 'react';

interface CustomerInfoProps {
  customerInfo?: CustomerInfoType;
  onNext: (data?: any) => void;
  onPrevious: () => void;
  onStepComplete: (data: any) => void;
}

export const CustomerInfo: React.FC<CustomerInfoProps> = ({
  customerInfo,
  onNext,
  onPrevious,
  onStepComplete,
}) => {
  const [customer, setCustomer] = useState<CustomerInfoType>(
    customerInfo || {
      id: '1',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  );

  const updateCustomer = (field: keyof CustomerInfoType, value: string) => {
    setCustomer(prev => ({ ...prev, [field]: value }));
  };

  const handleContinue = () => {
    const isValid =
      customer.firstName &&
      customer.lastName &&
      customer.email &&
      HelperMethods.isValidEmail(customer.email) &&
      customer.phoneNumber;

    if (isValid) {
      onStepComplete({ customerInfo: customer });
      onNext({ customerInfo: customer });
    }
  };

  const isFormValid =
    customer.firstName &&
    customer.lastName &&
    customer.email &&
    HelperMethods.isValidEmail(customer.email) &&
    customer.phoneNumber;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Your Information
        </h2>
        <p className="text-gray-600">
          Please provide your contact information for booking confirmation
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={customer.firstName}
              onChange={e => updateCustomer('firstName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !customer.firstName
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="Enter your first name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              value={customer.lastName}
              onChange={e => updateCustomer('lastName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !customer.lastName
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="Enter your last name"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={customer.email}
              onChange={e => updateCustomer('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !customer.email ||
                (customer.email && !HelperMethods.isValidEmail(customer.email))
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="Enter your email address"
              required
            />
            {customer.email && !HelperMethods.isValidEmail(customer.email) && (
              <p className="mt-1 text-sm text-red-600">
                Please enter a valid email address
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={customer.phoneNumber}
              onChange={e => {
                // Only allow numbers
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                updateCustomer('phoneNumber', numericValue);
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !customer.phoneNumber
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="(555) 123-4567"
              required
            />
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          onClick={onPrevious}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Previous
        </button>
        <button
          onClick={handleContinue}
          disabled={!isFormValid}
          className={`px-6 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isFormValid
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};
